/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FormikValues } from 'formik';
import i18n from 'i18n';
import { keyBy, mapValues } from 'lodash';
import {
  array,
  boolean,
  lazy,
  mixed,
  number,
  object,
  ObjectSchema,
  string,
} from 'yup';

import step1 from 'assets/icons/declaration-form-step1.svg';
import step2 from 'assets/icons/declaration-form-step2.svg';
import step3 from 'assets/icons/declaration-form-step3.svg';
import step4 from 'assets/icons/declaration-form-step4.svg';
import { getFormatedDates } from 'core/utils/date.helpers';
import {
  ChallengesType,
  IChallengeListProductItem,
  IFormInputGroup,
} from 'interfaces';
import { STEPS } from './constants';
import { DeclarationFormFinalStep } from './DeclarationFormFinalStep';
import { DeclarationFormProductsStep } from './DeclarationFormProductsStep';
import { DeclarationFormProofStep } from './DeclarationFormProofStep';
import { DeclarationFormQuestionsStep } from './DeclarationFormQuestionsStep';
import { IFormProduct, IFormProducts } from './interfaces';

export const getFormSteps = ({
  needProof,
  declarationForm,
  resultEntryEnabled,
  predefinedQuestions,
}: ChallengesType) => {
  const distributorData = predefinedQuestions?.find(
    (item) => item.key === 'distributor',
  );
  const invoiceDateStartData = predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateStart',
  );
  const invoiceDateEndData = predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateEnd',
  );
  return [
    ...(resultEntryEnabled
      ? [
          {
            component: DeclarationFormProductsStep,
            stepper: {
              name: i18n.t('challenges.declaration.steps.results'),
              icon: <img src={step1} alt="loudspeaker" />,
            },
          },
        ]
      : []),
    ...(needProof
      ? [
          {
            component: DeclarationFormProofStep,
            stepper: {
              name: i18n.t('challenges.declaration.steps.proof'),
              icon: <img src={step2} alt="camera" />,
            },
          },
        ]
      : []),
    ...(declarationForm ||
    (predefinedQuestions && distributorData?.isDisplayed) ||
    (predefinedQuestions && invoiceDateStartData?.isDisplayed) ||
    (predefinedQuestions && invoiceDateEndData?.isDisplayed)
      ? [
          {
            component: DeclarationFormQuestionsStep,
            stepper: {
              name: i18n.t('challenges.declaration.steps.questions'),
              icon: <img src={step3} alt="asking" />,
            },
          },
        ]
      : []),
    {
      component: DeclarationFormFinalStep,
      stepper: {
        name: i18n.t('challenges.declaration.steps.send'),
        icon: <img src={step4} alt="sending" />,
      },
    },
  ];
};

export const makeInitialValues = (
  {
    challengeListProducts,
    needCurrencyValue,
    needProof,
    declarationForm,
    predefinedQuestions,
  }: ChallengesType,
  oldFormiikValues: FormikValues | undefined,
): FormikValues => ({
  [STEPS.products]: mapValues(
    keyBy(challengeListProducts, 'challengeProduct.@id'),
    ({ challengeProduct }: IChallengeListProductItem): IFormProduct => {
      const oldValue =
        oldFormiikValues?.[STEPS.products]?.[challengeProduct['@id']];
      return {
        id: challengeProduct['@id'],
        ...(oldValue ?? {
          isEditing: false,
          isSelected: false,
          units: '',
          selectedUnits: 0,
          ...(needCurrencyValue && { value: '', selectedValue: 0 }),
        }),
      };
    },
  ),
  ...(needProof && { [STEPS.file]: undefined }),
  ...(declarationForm && {
    [STEPS.answers]: declarationForm.formInputGroups?.map(
      ({ formInputs }: IFormInputGroup) => ({
        id: formInputs[0]['@id'],
        type: formInputs[0].type,
        answer: '',
      }),
    ),
  }),
  ...(predefinedQuestions && {
    distributor: '',
    invoiceDateStart: '',
    invoiceDateEnd: '',
  }),
});

// 100Mo upload file size restriction
const PROOF_MAX_SIZE = 100000000;
const PROOF_ACCEPTED_FORMATS = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const makeValidationSchema = ({
  needCurrencyValue,
  needProof,
  declarationForm,
  predefinedQuestions,
}: ChallengesType & { predefinedQuestions: any[] }): ObjectSchema => {
  const distributorData = predefinedQuestions?.find(
    (item) => item.key === 'distributor',
  );
  const invoiceDateStartData = predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateStart',
  );
  const invoiceDateEndData = predefinedQuestions?.find(
    (item) => item.key === 'invoiceDateEnd',
  );
  const schema = object().shape({
    [STEPS.products]: lazy((value: any) =>
      object().shape(
        mapValues(value, () =>
          object().shape({
            isEditing: boolean()
              .oneOf([false])
              .required(i18n.t('challenges.declaration.productCardError')),
            units: number().when(['isEditing'], {
              is: true,
              then: number().required(
                i18n.t('challenges.declaration.productCardError'),
              ),
            }),
            ...(needCurrencyValue && {
              value: number().when(['isEditing'], {
                is: true,
                then: number().required(
                  i18n.t('challenges.declaration.productCardError'),
                ),
              }),
            }),
          }),
        ),
      ),
    ),
    ...(needProof && {
      [STEPS.file]: mixed()
        .test(
          'required-file',
          i18n.t('challenges.declaration.questions.error'),
          (value: unknown) => value instanceof File && value.size > 0,
        )
        .test(
          'fileSize',
          i18n.t('challenges.declaration.fileSizeError'),
          (value: unknown) =>
            !(value instanceof File) || value.size <= PROOF_MAX_SIZE,
        )
        .test(
          'fileType',
          i18n.t('challenges.declaration.fileTypeError'),
          (value: unknown) =>
            !(value instanceof File) ||
            PROOF_ACCEPTED_FORMATS.includes(value.type),
        ),
    }),
    ...(declarationForm && {
      [STEPS.answers]: array().of(
        object().shape({
          answer: string().required(
            i18n.t('challenges.declaration.questions.error'),
          ),
        }),
      ),
    }),
    ...(predefinedQuestions?.length > 0 &&
      distributorData?.isDisplayed && {
        distributor: string().required(
          i18n.t('challenges.declaration.questions.error'),
        ),
      }),
    ...(predefinedQuestions?.length > 0 &&
      invoiceDateStartData?.isDisplayed && {
        invoiceDateStart: string().required(
          i18n.t('challenges.declaration.questions.error'),
        ),
      }),
    ...(predefinedQuestions?.length > 0 &&
      invoiceDateEndData?.isDisplayed && {
        invoiceDateEnd: string()
          .required(i18n.t('challenges.declaration.questions.error'))
          .test(
            'is-after-start',
            i18n.t('challenges.declaration.isAfterInvoiceStartDate'),
            function (value) {
              const { invoiceDateStart } = this.parent;
              if (!value || !invoiceDateStart) return true;
              return new Date(value) >= new Date(invoiceDateStart);
            },
          ),
      }),
  });
  return schema;
};

export const getDates = (challengeData: any, language: string) => {
  return getFormatedDates(
    {
      challenge: {
        start: challengeData?.startDate,
        end: challengeData?.endDate,
      },
      diffusion: {
        start: challengeData?.startDiffusionDate,
        end: challengeData?.endDiffusionDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );
};

export const getSelectedProducts = (products?: IFormProducts): IFormProduct[] =>
  Object.values(products ?? {}).filter(
    ({ isSelected }: { isSelected: boolean }) => isSelected,
  ) || [];

export const actionsModerationTypeEnum = Object.freeze({
  NONE: 'none',
  ADMIN: 'admin',
  OCR: 'ocr',
});
